import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { CommunityMember } from "@circle-react/types";
import { omit, sortBy } from "lodash";
import { useMemo } from "react";
import { keepPreviousData, useQuery } from "react-query";

interface HydrationParams {
  excludeAdmins?: boolean;
  filters?: Record<string, unknown>;
  ids: (number | string)[];
  includeInactive?: boolean;
  valueField?: "id" | "user_id";
}

interface HydrationResponse {
  records: CommunityMember[];
}

interface HydrationRequestBody {
  filters: Record<string, unknown>;
  per_page: number;
}

// POST avoids URL length limits and matches the backend controller's
// expectation that filters are in the request body.
export const useCommunityMemberHydration = ({
  excludeAdmins = false,
  filters,
  ids,
  includeInactive = false,
  valueField = "id",
}: HydrationParams) => {
  const numericIds = useMemo(
    () =>
      ids.flatMap(id => {
        if (typeof id === "number") return [id];
        return /^\d+$/.test(id) ? [Number(id)] : [];
      }),
    [ids],
  );
  const sortedKey = sortBy(numericIds).join(",");

  const filtersKey = useMemo(
    () => (filters ? JSON.stringify(filters) : null),
    [filters],
  );

  const hydrationFilters = useMemo(() => {
    const rest = omit(filters ?? {}, "exclude_ids");
    return {
      exclude_admins: excludeAdmins,
      include_inactive: includeInactive,
      ...rest,
      [valueField]: numericIds,
    };
  }, [excludeAdmins, filters, includeInactive, numericIds, valueField]);

  const { data, isPending } = useQuery<CommunityMember[]>({
    queryKey: [
      "community-members",
      "hydrate",
      sortedKey,
      valueField,
      includeInactive,
      excludeAdmins,
      filtersKey,
    ],
    queryFn: async () => {
      const response = await reactQueryPost<
        HydrationResponse,
        HydrationRequestBody
      >(internalApi.search.communityMembers.index({}), {
        filters: hydrationFilters,
        per_page: numericIds.length,
      });
      return response.records;
    },
    enabled: numericIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  return { hydratedMembers: data ?? [], isHydrationLoading: isPending };
};
